import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import LoyaltyLandingPageMovingTiersSection from 'loyalty/pages/loyaltyLandingPageSharedSections/LoyaltyLandingPageMovingTiersSection'
import LoyaltyLandingPageDiscoverTiersSection from 'loyalty/pages/loyaltyLandingPageSharedSections/LoyaltyLandingPageDiscoverTiersSection'
import LoyaltyLandingPageBenefitsTableSection from 'loyalty/pages/loyaltyLandingPageSharedSections/LoyaltyLandingPageBenefitsTableSection'
import LoyaltyLandingPageFaqSection from 'loyalty/pages/loyaltyLandingPageSharedSections/LoyaltyLandingPageFaqSection'
import { useIntl } from 'react-intl'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import LoyaltyJoinModal from 'loyalty/components/LoyaltyJoinModal'
import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import useLoyaltyActiveChooseDoubleRewards from './hooks/useLoyaltyActiveChooseDoubleRewards'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import { scrollToId } from 'lib/window/scrollUtils'
import LoyaltyChooseDoubleRewardsLandingPageHero from './components/LoyaltyChooseDoubleRewardsLandingPageHero'
import LoyaltyChooseDoubleRewardsLandingPageHowItWorks from './components/LoyaltyChooseDoubleRewardsLandingPageHowItWorks'
import LoyaltyChooseDoubleRewardsLandingPageChoices from './components/LoyaltyChooseDoubleRewardsLandingPageChoices'
import { CHOOSE_DOUBLE_REWARDS_CAMPAIGN_TAG, CHOOSE_DOUBLE_REWARDS_OFFERS_ANCHOR } from './loyaltyChooseDoubleRewardsTakeover.constants'
import TextButton from 'LuxKit/Button/TextButton'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import useLoyaltyChooseDoubleRewardsRegion from './hooks/useLoyaltyChooseDoubleRewardsRegion'
import useRegionAwareOfferList from 'hooks/Offers/useRegionAwareOfferList'
import useDelayedJumpToHash from 'hooks/useDelayedJumpToHash'
import useCruiseInternationalMarket from 'hooks/useCruiseInternationalMarket'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import { CRUISE_BANK_UNIFIED_FILTERS } from 'constants/cruise'
import { getToursOfferListFilters, isInternationalLuxuryEscapesHero } from 'home/helpers'
import useLoyaltyChooseDoubleRewardsTracking from './hooks/useLoyaltyChooseDoubleRewardsTracking'

function LoyaltyChooseDoubleRewardsLandingPage() {
  const { track } = useLoyaltyChooseDoubleRewardsTracking('landing_cta')
  const intl = useIntl()
  const programName = getLoyaltyProgramDisplayName()
  const dispatch = useAppDispatch()

  useLoyaltyConfig()
  const showModal = useContext(ModalContext)
  const loggedIn = useAppSelector(selectLoggedIn)
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const account = useAppSelector(state => state.loyalty.account)
  const { needsRegionSwitch, switchToLoyaltyRegion, switchRegionLabel } = useLoyaltyChooseDoubleRewardsRegion()
  const accountLoading = useAppSelector(store => store.loyalty.account.initial || store.loyalty.account.fetching)
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)

  // Match HotelsPage's primary carousel sorting for the current market.
  let hotelSort: App.OfferListFilters['sortBy'] = 'homepage'
  if (isInternationalLuxuryEscapesHero(regionCode)) {
    hotelSort = 'home'
  } else if (isManualSortingEnabledFor('hotels')) {
    hotelSort = 'vertical-hotel'
  }

  // Campaign-specific LPC filters: hotel sorting with 5 regional / 15 anywhere requests.
  const lpcFilters = useMemo((): App.OfferListFilters => ({
    offerTypes: ['tactical_ao_hotel', 'rental'],
    campaigns: [CHOOSE_DOUBLE_REWARDS_CAMPAIGN_TAG],
    sortBy: hotelSort,
  }), [hotelSort])
  const lpcRegionRequest = useMemo(() => ({ filters: lpcFilters, limit: 5 }), [lpcFilters])
  const lpcAnywhereRequest = useMemo(() => ({ filters: lpcFilters, limit: 15 }), [lpcFilters])

  // Reuse the homepage TourOfferCarousel's filter derivation.
  const isToursManualSortingEnabled = isManualSortingEnabledFor('tours')
  const tourFilters = useMemo(() => getToursOfferListFilters(isToursManualSortingEnabled), [isToursManualSortingEnabled])
  const tourViewAllFilters = useMemo((): App.OfferListFilters => ({
    ...tourFilters,
    sortBy: undefined,
  }), [tourFilters])

  // Match the homepage CruiseOfferCarousel's cruise-bank filters and sorting rules.
  const isInternationalCruiseMarket = useCruiseInternationalMarket()
  const isCruisesManualSortingEnabled = isManualSortingEnabledFor('cruises')
  const cruiseFilters = useMemo((): App.OfferListFilters => ({
    ...CRUISE_BANK_UNIFIED_FILTERS,
    frontPageVisibleOnly: true,
    sortBy: isInternationalCruiseMarket || !isCruisesManualSortingEnabled ? undefined : CRUISE_BANK_UNIFIED_FILTERS.sortBy,
    limit: 20,
  }), [isInternationalCruiseMarket, isCruisesManualSortingEnabled])
  const cruiseViewAllFilters = useMemo((): App.OfferListFilters => ({
    ...cruiseFilters,
    sortBy: undefined,
  }), [cruiseFilters])

  const rewardState = useLoyaltyActiveChooseDoubleRewards()
  useDelayedJumpToHash(1000, -80)

  const [hotelFilter, setHotelFilter] = useState<string | undefined>('hotel')
  const lpcOfferList = useRegionAwareOfferList(lpcRegionRequest, lpcAnywhereRequest, undefined, {
    disabled: hotelFilter !== 'tactical_ao_hotel' || !rewardState,
    // Rental is outside the offer types that turn this on automatically, and adding it
    // would otherwise let unpriced LPC hotels through.
    filterUnpriced: true,
  })

  // Apply HotelsPage's fallback visibility to flash; LPC uses the campaign filters above.
  const hotelFilters = useMemo((): App.OfferListFilters => (
    hotelFilter === 'tactical_ao_hotel'
      ? { ...lpcFilters, limit: 20 }
      : {
          offerTypes: ['hotel'],
          sortBy: hotelSort,
          frontPageVisibleOnly: hotelSort === 'homepage' ? true : undefined,
          limit: 20,
        }
  ), [hotelFilter, lpcFilters, hotelSort])

  // View all opens hotel search with the campaign tag. Carousel sort values such as
  // vertical-hotel leave search loading forever, so they stay off every view-all link.
  const hotelViewAllFilters = useMemo((): App.OfferListFilters => ({
    ...hotelFilters,
    campaigns: [CHOOSE_DOUBLE_REWARDS_CAMPAIGN_TAG],
    sortBy: undefined,
  }), [hotelFilters])

  const hotelFilterLabel = intl.formatMessage({
    id: 'evJ7paB6kzAbbxPBr_f_',
    defaultMessage: 'Exclusive offers',
    description: 'Filter pill for exclusive hotel offers on the double rewards campaign landing page',
  })
  const tacticalHotelFilterLabel = intl.formatMessage({
    id: 'dPHExSoPd4ANPCRxuLfn',
    defaultMessage: 'Great value hotels',
    description: 'Filter pill for great value hotels on the double rewards campaign landing page',
  })

  const hotelFilterOptions = useMemo(() => [
    { value: 'hotel', label: hotelFilterLabel },
    { value: 'tactical_ao_hotel', label: tacticalHotelFilterLabel },
  ], [hotelFilterLabel, tacticalHotelFilterLabel])

  const joinLabel = intl.formatMessage({
    id: 'h6PuS4suQF1JuReZxhxN',
    defaultMessage: "Join {programName} now - it's free",
    description: 'Join membership action shared by all sections of the double rewards landing page',
  }, { programName })

  const onClickSharedCta = useCallback(async() => {
    track('clicked')
    if (needsRegionSwitch) {
      switchToLoyaltyRegion()
      return
    }
    if (!loggedIn) {
      track('sign_in_clicked')
      // Login modal
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal skipLoyaltyAutoEnrolment />)
      if (result?.loggedIn) {
        dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
      }
      return
    }

    if (!account.data) {
      track('join_clicked')
      // Enrol join modal
      const confirmed = await showModal<boolean>(
        <LoyaltyJoinModal onAfterClose={undefined} primaryActionCta={joinLabel} />,
      )
      if (confirmed) dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
      return
    }

    // Attempt rewards enrolment
    scrollToId('choose-double-rewards', -80)
  }, [loggedIn, account.data, showModal, dispatch, joinLabel, needsRegionSwitch, switchToLoyaltyRegion, track])

  if (!rewardState) return null

  const isMember = loggedIn && hasLoyaltyAccount
  const showCta = needsRegionSwitch || !isMember || rewardState.memberState.status !== 'enrolled'
  const ctaDisabled = (loggedIn && accountLoading) || (!needsRegionSwitch && isMember && !rewardState.canEnrol)

  const chooseCtaLabel = intl.formatMessage({
    id: 'U388ZZx5Sia8xQhHlAlM',
    defaultMessage: 'Choose your reward',
    description: 'Button on the double rewards homepage banner opening the reward selection',
  })

  let sharedCtaLabel = chooseCtaLabel
  if (needsRegionSwitch) {
    sharedCtaLabel = switchRegionLabel
  }

  const ctaBtn = showCta ? (
    <TextButton kind="primary" shape="round" onClick={onClickSharedCta} disabled={ctaDisabled}>
      {sharedCtaLabel}
    </TextButton>
  ) : undefined

  const hotelsTitle = intl.formatMessage({
    id: '4a2COVlm-HaTX5GRtaPn',
    defaultMessage: 'Hotel offers',
    description: 'Hotel offers carousel title on the double rewards campaign landing page',
  })

  const toursTitle = intl.formatMessage({
    id: 'Ga27MvTjuqaFRrYhH58S',
    defaultMessage: 'Tour offers',
    description: 'Tour offers carousel title on the double rewards campaign landing page',
  })

  const cruisesTitle = intl.formatMessage({
    id: 'AKTXFWmxc1c2cCxTcwjO',
    defaultMessage: 'Cruise offers',
    description: 'Cruise offers carousel title on the double rewards campaign landing page',
  })

  return (
    <>
      <LoyaltyChooseDoubleRewardsLandingPageHero cta={ctaBtn} />
      <LoyaltyChooseDoubleRewardsLandingPageHowItWorks onClickSharedCta={onClickSharedCta} cta={ctaBtn} />
      <LoyaltyChooseDoubleRewardsLandingPageChoices onClickSharedCta={onClickSharedCta} />
      {tier !== 'infinite' && <>
        {loggedIn && account.data ? <LoyaltyLandingPageMovingTiersSection background="none" /> : <LoyaltyLandingPageDiscoverTiersSection background="none" />}
        <LoyaltyLandingPageBenefitsTableSection
          background="none"
          tableBackground="light-background"
          highlightTier={loggedIn && account.data ? tier : undefined}
        />
      </>}
      <OfferCardCarouselSection
        id={CHOOSE_DOUBLE_REWARDS_OFFERS_ANCHOR}
        title={hotelsTitle}
        filters={hotelFilters}
        overrideOfferList={hotelFilter === 'tactical_ao_hotel' ? lpcOfferList : undefined}
        allLink={`/search?${getSearchQueryFromFilter(hotelViewAllFilters)}`}
        additionalFilters="custom"
        additionalFilterOptions={hotelFilterOptions}
        defaultFilter="hotel"
        selectedFilter={hotelFilter}
        onSelectedFilterChange={setHotelFilter}
        tileStyle="card-default"
        tileSize="medium"
      />
      <OfferCardCarouselSection
        title={toursTitle}
        filters={tourFilters}
        allLink={`/search/tours?${getSearchQueryFromFilter(tourViewAllFilters)}`}
        hideWhenEmpty
        tileStyle="card-default"
        tileSize="medium"
      />
      <OfferCardCarouselSection
        title={cruisesTitle}
        filters={cruiseFilters}
        allLink={`/search/cruises?${getSearchQueryFromFilter(cruiseViewAllFilters)}`}
        hideWhenEmpty
        tileStyle="card-default"
        tileSize="medium"
      />
      <LoyaltyLandingPageFaqSection slug={isMember ? (tier === 'infinite' ? 'societe-infinite-faqs' : 'societe-faqs-member') : 'societe-faqs'} />
    </>
  )
}

export default LoyaltyChooseDoubleRewardsLandingPage
